import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import {
  Container, Card, CardHeader, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  CircularProgress, Chip, Box, Alert
} from '@mui/material';
import { useAuth } from './AuthContext';

// Helper to format Firestore Timestamps
const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) {
    return 'N/A';
  }
  return new Date(timestamp.seconds * 1000).toLocaleString();
};

// No duplicated lease duration: the worker stamps leaseExpiresAt on the document,
// so this only has to compare that timestamp against the current time.
const effectiveStatus = (job) =>
  job.status === 'running' && job.leaseExpiresAt?.toDate() < new Date()
    ? 'failed'
    : job.status;

// Helper to style the status badge
const StatusChip = ({ status }) => {
  let color = 'default';
  switch (status) {
    case 'completed':
      color = 'success';
      break;
    case 'failed':
      color = 'error';
      break;
    case 'running':
      color = 'info';
      break;
    default:
      color = 'secondary';
  }
  return <Chip label={status} color={color} size="small" />;
};

export default function JobsListPage() {
  const { currentUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // A clock tick is required by the stale-lease finding; Firestore events alone cannot re-render at expiry.
  const [leaseClock, setLeaseClock] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    
    const jobsCollection = collection(db, 'jobs');
    const q = query(
      jobsCollection, 
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const jobsData = [];
        querySnapshot.forEach((doc) => {
          jobsData.push({ id: doc.id, ...doc.data() });
        });
        setJobs(jobsData);
        setLoading(false);
      }, 
      (err) => {
        console.error(err);
        setError('Failed to fetch jobs in real-time.');
        setLoading(false);
      }
    );

    return () => unsubscribe();

  }, [currentUser]);

  useEffect(() => {
    const now = Date.now();
    const nextExpiry = jobs
      .filter((job) => job.status === 'running')
      .map((job) => job.leaseExpiresAt?.toDate().getTime())
      .filter((expiresAt) => Number.isFinite(expiresAt) && expiresAt > now)
      .reduce((earliest, expiresAt) => Math.min(earliest, expiresAt), Infinity);

    if (!Number.isFinite(nextExpiry)) return undefined;

    const timerId = setTimeout(
      () => setLeaseClock((tick) => tick + 1),
      nextExpiry - now + 1
    );
    return () => clearTimeout(timerId);
  }, [jobs, leaseClock]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Card>
        <CardHeader title="Your Query Generation Jobs" />
        <CardContent>
          {error && <Alert severity="error">{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="jobs table">
                <TableHead>
                  <TableRow>
                    <TableCell>URL</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.length > 0 ? (
                    jobs.map((job) => (
                      <TableRow
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        hover
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{job.url}</TableCell>
                        <TableCell>{job.product_category}</TableCell>
                        <TableCell>{formatDate(job.createdAt)}</TableCell>
                        <TableCell><StatusChip status={effectiveStatus(job)} /></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No jobs found.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
